import type { FieldValues, Resolver } from "react-hook-form";
import { z } from "zod";

export type Severity = "error" | "requiredForApproval" | "partial";

export const SEVERITY_RANK: Record<Severity, number> = {
	error: 2,
	requiredForApproval: 1,
	partial: 0,
};

type NormalizeOptions = {
	returnEmptyObject?: boolean;
	returnEmptyArray?: boolean;
	emptyValue?: null | undefined;
};

const isPlainObject = (value: object): boolean => {
	const prototype = Object.getPrototypeOf(value);
	return prototype === Object.prototype || prototype === null;
};

export const normalizeFormValues = (value: unknown, options: NormalizeOptions = {}): unknown => {
	const empty = "emptyValue" in options ? options.emptyValue : null;
	if (value === undefined || value === null || value === "") return empty;
	if (Array.isArray(value)) {
		const mapped = value.map((item) => normalizeFormValues(item, options));
		if (mapped.length === 0 && !options.returnEmptyArray) return empty;
		return mapped;
	}
	if (typeof value === "object") {
		// Class instances (Luxon DateTime, Date, File, ...) must pass through untouched.
		// Recursing copies their own enumerable props onto a plain object, stripping the
		// prototype and breaking schema transforms that call methods on them.
		if (!isPlainObject(value)) return value;
		const result: Record<string, unknown> = {};
		for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
			result[key] = normalizeFormValues(v, options);
		}
		if (Object.keys(result).length === 0 && !options.returnEmptyObject) return empty;
		const allEmpty = Object.values(result).every((v) => v === null || v === undefined);
		if (allEmpty && !options.returnEmptyObject) return empty;
		return result;
	}
	return value;
};

const setNestedPath = (obj: Record<string, unknown>, path: PropertyKey[], value: unknown) => {
	let current = obj as Record<string, unknown>;
	for (let i = 0; i < path.length - 1; i++) {
		const key = String(path[i]);
		if (current[key] === undefined || current[key] === null) {
			current[key] = typeof path[i + 1] === "number" ? [] : {};
		}
		current = current[key] as Record<string, unknown>;
	}
	current[String(path[path.length - 1])] = value;
};

export const getNestedError = (obj: Record<string, unknown>, path: string): unknown => {
	return path.split(".").reduce<unknown>((current, key) => {
		if (current && typeof current === "object") return (current as Record<string, unknown>)[key];
		return undefined;
	}, obj);
};

type FormResolverOptions = {
	emptyValue?: null | undefined;
	// Opt in to honoring RHF's options.names (set by trigger(fieldPaths) / per-field onBlur),
	// surfacing only errors for the validated fields + descendants. Off by default so a
	// SectionNavLayout-style handleSubmit still surfaces hard errors for unmounted steps.
	scopeToNames?: boolean;
};

const issueInScope = (path: string, names: string[] | undefined): boolean =>
	!names || names.some((n) => path === n || path.startsWith(`${n}.`) || n.startsWith(`${path}.`));

export const createFormResolver = <TInput extends FieldValues>(
	schema: z.ZodType,
	resolverOptions?: FormResolverOptions,
): Resolver<TInput> => {
	return async (values, _context, options) => {
		const cleaned = normalizeFormValues(values, {
			returnEmptyObject: true,
			returnEmptyArray: true,
			...(resolverOptions != null && "emptyValue" in resolverOptions
				? { emptyValue: resolverOptions.emptyValue }
				: {}),
		});
		const result = await schema.safeParseAsync(cleaned);
		if (result.success) {
			return { values: result.data as unknown as TInput, errors: {} };
		}

		const names = resolverOptions?.scopeToNames ? options?.names : undefined;
		const errors: Record<string, unknown> = {};

		for (const issue of result.error.issues) {
			if (issue.path.length === 0) continue;
			if (!issueInScope(issue.path.join("."), names)) continue;
			setNestedPath(errors, issue.path, { type: "error", message: issue.message });
		}

		return { values: {} as TInput, errors };
	};
};
